import React, { useState } from 'react';
import { useAppContext } from 'src/components/app-context';
import { TitleMediumText } from '@prisjakt/ui/text/variants';
import { SizeVariantSelectionButton } from './size-variant-selection-button';
import { useOfferListFilterContext } from 'src/areas/product/components/offer-list-filter-context';
import { IVariantsProps, IVariantValue } from '../types';
import { FilterType } from 'src/components/price-list/types';
import { getGroupedVariants } from './utils/group-size-variants';
import {
  VariantGridTitle,
  VariantGridHeader,
  ScrollableArea,
} from '../styles-common';
import {
  StyledShowMoreButton,
  VariantGridCell,
  VariantGridWrapper,
} from './styles';

export const SizeVariantsGrid: React.FC<IVariantsProps> = props => {
  const { title, variants, handleSelection, isMobile } = props;
  const { t } = useAppContext();
  const { getFilter } = useOfferListFilterContext();
  const [isExpanded, setIsExpanded] = useState(false);

  const filter = getFilter(FilterType.VARIANT_SIZE);
  const selection = filter?.active ? filter?.selection : undefined;

  const groupedVariants = getGroupedVariants(variants?.filter(Boolean));

  const LIMIT_CELLS = isMobile ? 12 : 15;

  const selectAllCell = (
    <VariantGridCell>
      <SizeVariantSelectionButton
        handleSelection={handleSelection}
        title={t('product', 'variants.all')}
        selection={selection}
        variant={null}
        isMobile={isMobile}
      />
    </VariantGridCell>
  );

  const getSelectedSizesAsVariants = (variantGroup: IVariantValue[]) => {
    function findSizeInVariantGroup(size: string) {
      return variantGroup.find(variant => variant.size === size);
    }

    const selectedSizes = selection?.filter(findSizeInVariantGroup) || [];

    return selectedSizes.map(size => ({
      size,
      disabled: findSizeInVariantGroup(size)?.disabled!,
    }));
  };

  const removeOptionalsFromVariant = (variant: IVariantValue) => ({
    size: variant.size!,
    disabled: variant.disabled!,
  });

  const getSelectedAndFirstFewVariants = (variantGroup: IVariantValue[]) => {
    const selectedVariants = getSelectedSizesAsVariants(variantGroup);

    const numSelected = selectedVariants.length || 0;

    const variantIsNotSelected = (variant: IVariantValue) =>
      !selectedVariants.find(
        selectedVariant => selectedVariant.size === variant.size
      );

    const numNonSelectedVariantsToKeep = Math.max(0, LIMIT_CELLS - numSelected);

    const nonSelectedVariantsToKeep = variantGroup
      .filter(variantIsNotSelected)
      .slice(0, numNonSelectedVariantsToKeep)
      .map(removeOptionalsFromVariant);

    const result = selectedVariants.concat(nonSelectedVariantsToKeep);

    result.sort((a, b) => a.size!.localeCompare(b.size!));
    return result;
  };

  const variantGroupCells = (
    i: number,
    variantGroup: IVariantValue[],
    showSelectAll: boolean
  ) => {
    const variantsToDisplay = isExpanded
      ? variantGroup
      : getSelectedAndFirstFewVariants(variantGroup);

    return (
      <>
        <VariantGridWrapper key={i}>
          {showSelectAll && selectAllCell}
          {variantsToDisplay.map(sizeVariant => (
            <VariantGridCell key={sizeVariant.size}>
              <SizeVariantSelectionButton
                handleSelection={handleSelection}
                title={sizeVariant.size!}
                variant={sizeVariant.size!}
                selection={selection}
                disabled={sizeVariant.disabled}
                isMobile={isMobile}
              />
            </VariantGridCell>
          ))}
        </VariantGridWrapper>
        {variantGroup.length > LIMIT_CELLS && (
          <StyledShowMoreButton onClick={() => setIsExpanded(!isExpanded)}>
            {isExpanded
              ? t('common', 'button.show-less')
              : t('common', 'button.show-more')}
          </StyledShowMoreButton>
        )}
      </>
    );
  };

  return (
    <div>
      {title && (
        <VariantGridHeader>
          <VariantGridTitle>
            <TitleMediumText>{title}</TitleMediumText>
          </VariantGridTitle>
        </VariantGridHeader>
      )}
      <ScrollableArea>
        {groupedVariants.map((group, i) =>
          variantGroupCells(i, group, i === 0)
        )}
      </ScrollableArea>
    </div>
  );
};
